import type { EgressTargetDecision, EgressTargetDecisionReason } from '@nadoutong/contracts';
import { useT, type I18nKey, type TParams } from '../../i18n/index.ts';
import { Chip } from '../../ui/index.ts';
import { NodeIdentity } from './NodeIdentity.tsx';
import { UpstreamLabel } from './UpstreamLabel.tsx';
import type { NodeDecisionTree, TargetCandidate, UpstreamDecisionBranch } from './decisions.ts';

export function TargetDecisionTree({
  trees,
}: {
  readonly trees: readonly NodeDecisionTree[];
}): JSX.Element {
  const t = useT();
  if (trees.length === 0) {
    return <p className="m-0 text-dense text-paper-low">{t('egress_lines.traffic.empty')}</p>;
  }
  return (
    <div className="flex flex-col gap-5" data-testid="egress-lines-decision-forest">
      {trees.map((tree) => (
        <NodeTree key={tree.node.nodeId} tree={tree} />
      ))}
    </div>
  );
}

function NodeTree({ tree }: { readonly tree: NodeDecisionTree }): JSX.Element {
  const t = useT();
  const node = tree.node;
  return (
    <article
      className="rounded border border-line bg-well px-3 py-3"
      data-testid={`egress-lines-tree-${node.nodeId}`}
    >
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <NodeIdentity
          nodeId={node.nodeId}
          region={node.gatewayRegion}
          to={`/supply/egress-lines/${encodeURIComponent(node.nodeId)}`}
          testId={`egress-lines-tree-node-${node.nodeId}`}
        />
        <Chip tone={node.active ? 'ok' : 'neutral'} lamp={false}>
          {node.active ? t('egress_lines.matrix.serving') : t('egress_lines.matrix.historical')}
        </Chip>
        <span className="font-mono text-micro text-paper-low">
          {node.ageSeconds === null
            ? t('egress_lines.matrix.freshness_unknown')
            : t('egress_lines.matrix.freshness_ago', { seconds: String(node.ageSeconds) })}
        </span>
      </header>
      {tree.upstreams.length === 0 ? (
        <p className="m-0 text-dense text-paper-low">{t('egress_lines.traffic.empty')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {tree.upstreams.map((branch) => (
            <li key={branch.upstreamId}>
              <UpstreamBranch branch={branch} gatewayRegion={node.gatewayRegion} />
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function UpstreamBranch({
  branch,
  gatewayRegion,
}: {
  readonly branch: UpstreamDecisionBranch;
  readonly gatewayRegion: string;
}): JSX.Element {
  const t = useT();
  const dest = branch.upstreamRegion;
  const hop = dest === null || dest.length === 0
    ? t('egress_lines.decision.region_unknown')
    : dest === gatewayRegion
      ? t('egress_lines.decision.same_region')
      : t('egress_lines.decision.cross_region');
  return (
    <div
      className="border-l border-line pl-3"
      data-testid={`egress-lines-tree-upstream-${branch.upstreamId}`}
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <UpstreamLabel
          upstreamId={branch.upstreamId}
          name={branch.upstreamName}
          region={branch.upstreamRegion}
        />
        <span className="text-micro text-paper-low">{hop}</span>
      </div>
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-dense">
        <dt className="text-paper-low">{t('egress_lines.decision.live')}</dt>
        <dd className="m-0 font-mono text-paper">
          <span className="text-ok">{branch.selectedPathId}</span>
          <span className="ml-2 text-paper-mid">{reasonLabel(t, branch.decision)}</span>
        </dd>
        {branch.candidates.filter((row) => row.pathId !== branch.selectedPathId).map((row) => (
          <CandidateRow key={row.pathId} row={row} />
        ))}
        <dt className="text-paper-low">{t('egress_lines.decision.health')}</dt>
        <dd className="m-0 text-paper-mid">{healthLine(t, branch.selected)}</dd>
      </dl>
    </div>
  );
}

function CandidateRow({ row }: { readonly row: TargetCandidate }): JSX.Element {
  const t = useT();
  const observation = row.observation;
  const rate = observation.success_rate;
  const detail = [
    observation.sample_count > 0
      ? t('egress_lines.decision.samples', { count: String(observation.sample_count) })
      : t('egress_lines.decision.no_samples'),
    rate === null ? null : t('egress_lines.decision.success', { pct: String(Math.round(rate * 100)) }),
  ].filter((part): part is string => part !== null).join(' · ');
  return (
    <>
      <dt className="text-paper-low">{t('egress_lines.decision.candidate')}</dt>
      <dd className="m-0 font-mono text-paper-mid">
        <span>{row.pathId}</span>
        <span className="ml-2 text-micro">{detail}</span>
      </dd>
    </>
  );
}

function reasonLabel(
  t: (key: I18nKey, params?: TParams) => string,
  decision: EgressTargetDecision | null,
): string {
  if (decision === null) return t('egress_lines.decision.reason.unknown');
  const reason = decision.reason;
  if (reason === 'faster') {
    return t('egress_lines.decision.reason.faster', {
      selected: formatMs(decision.selected_sort_key_ms),
      vs: formatMs(decision.vs_sort_key_ms),
      path: decision.vs_path_id ?? 'direct',
    });
  }
  if (reason === 'hold_margin') {
    return t('egress_lines.decision.reason.hold_margin', {
      selected: formatMs(decision.selected_sort_key_ms),
      vs: formatMs(decision.vs_sort_key_ms),
    });
  }
  if (reason === 'hold_dwell') {
    return t('egress_lines.decision.reason.hold_dwell', {
      vs: decision.vs_path_id ?? '—',
    });
  }
  return t(reasonKey(reason));
}

function reasonKey(reason: EgressTargetDecisionReason): I18nKey {
  if (reason === 'same_region_direct') return 'egress_lines.decision.reason.same_region_direct';
  if (reason === 'no_evidence_direct') return 'egress_lines.decision.reason.no_evidence_direct';
  if (reason === 'pinned') return 'egress_lines.decision.reason.pinned';
  if (reason === 'breaker_open') return 'egress_lines.decision.reason.breaker_open';
  if (reason === 'hold_unopposed') return 'egress_lines.decision.reason.hold_unopposed';
  if (reason === 'bootstrap') return 'egress_lines.decision.reason.bootstrap';
  if (reason === 'recovery') return 'egress_lines.decision.reason.recovery';
  return 'egress_lines.decision.reason.unknown';
}

function healthLine(
  t: (key: I18nKey, params?: TParams) => string,
  selected: TargetCandidate | null,
): string {
  if (selected === null) return t('egress_lines.decision.health_empty');
  const observation = selected.observation;
  const rate = observation.success_rate;
  const ttfb = observation.p50_ttfb_ms;
  const parts = [
    t('egress_lines.decision.samples', { count: String(observation.sample_count) }),
    rate === null ? null : t('egress_lines.decision.success', { pct: String(Math.round(rate * 100)) }),
    ttfb === null ? null : t('egress_lines.decision.ttfb', { ms: String(Math.round(ttfb)) }),
  ];
  return parts.filter((part): part is string => part !== null).join(' · ');
}

function formatMs(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(value)}ms`;
}
